import type { ReactElement } from "react";
import {
  MoneyValue,
  STAT_UNIT_STYLE,
} from "@/modules/dashboard/components/redesign/MoneyValue";
import type { DashboardStoreStatsCardProps } from "@/modules/dashboard/types/dashboard-analytics.types";

/** Never let a non-zero row collapse to an invisible sliver. */
const MIN_BAR_PERCENT = 4;

/** Below this share a rail cannot hold its percentage without clipping. */
const MIN_LABELLED_SHARE = 0.14;

/**
 * Store statistics for the range — delivered orders, average order value,
 * customers, checkout completion, catalogue health and rating — followed by the
 * ranked governorates and the payment-rail split, side by side in ONE card.
 *
 * The two blocks share a card because they answer the same question from two
 * angles ("where did the collected money come from — which place, which rail"),
 * and splitting them across two cards made the page read as two unrelated
 * reports. They sit in a 2-column grid from `lg` and stack below it.
 *
 * Mobile-first: two tiles across at 375px (these are short figures, so a 2-up
 * grid stays scannable and avoids a very tall column), three across from `lg`.
 */
export function DashboardStoreStatsCard({
  title,
  subtitle,
  stats,
  governorates,
  paymentMix,
}: DashboardStoreStatsCardProps): ReactElement {
  // Widths are proportional to the LARGEST row, not to the total: this ranks
  // magnitudes against each other, it is not a composition of a whole.
  const maxSales = governorates
    ? Math.max(...governorates.rows.map((row) => row.sales), 0)
    : 0;
  const mixTotal = paymentMix
    ? paymentMix.slices.reduce((sum, slice) => sum + slice.sales, 0)
    : 0;

  return (
    <section className="rounded-[18px] border border-[color:var(--line2)] bg-[color:var(--card)] p-4 sm:p-[18px]">
      <div className="min-w-0">
        <h2 className="text-[15px] font-bold text-[color:var(--text-primary)]">
          {title}
        </h2>
        <p className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
          {subtitle}
        </p>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
        {stats.map((stat) => (
          <div
            key={stat.id}
            className="min-w-0 rounded-[12px] border border-[color:var(--line)] bg-[color:var(--soft)] px-3 py-3"
          >
            <dt className="truncate text-[11.5px] text-[color:var(--text-muted)]">
              {stat.label}
            </dt>
            <dd className="mt-1 truncate text-[18px] font-bold leading-[1.4] text-[color:var(--text-primary)]">
              {/* Only the money stat carries a unit. Counts (delivered orders,
                  customers), percentages and the rating are bare figures —
                  appending a symbol to them would be a lie, so `currency` is
                  absent rather than empty for those. */}
              {stat.currency ? (
                <MoneyValue
                  amount={stat.value}
                  {...stat.currency}
                  unitStyle={STAT_UNIT_STYLE}
                />
              ) : (
                <bdi>{stat.value}</bdi>
              )}
            </dd>
            {stat.hint ? (
              <p className="mt-1 text-[10.5px] leading-[1.4] text-[color:var(--text-muted)]">
                {stat.hint}
              </p>
            ) : null}
          </div>
        ))}
      </dl>

      {governorates || paymentMix ? (
        <div className="mt-5 grid grid-cols-1 gap-6 border-t border-[color:var(--hair)] pt-4 lg:grid-cols-2 lg:gap-7">
          {governorates ? (
            <div className="min-w-0">
              <h3 className="text-[13px] font-bold text-[color:var(--text-primary)]">
                {governorates.title}
              </h3>
              <p className="mt-0.5 text-[11.5px] text-[color:var(--text-muted)]">
                {governorates.subtitle}
              </p>

              {governorates.rows.length === 0 ? (
                <p className="mt-2 text-[11.5px] leading-[1.5] text-[color:var(--text-muted)]">
                  {governorates.emptyLabel}
                </p>
              ) : (
                <ul className="mt-2.5 flex flex-col gap-2.5">
                  {governorates.rows.map((row) => {
                    const percent =
                      maxSales > 0
                        ? Math.max(MIN_BAR_PERCENT, (row.sales / maxSales) * 100)
                        : MIN_BAR_PERCENT;
                    const orders = governorates.ordersLabel(row.orders);
                    const summary = governorates.barSummary(
                      row.name,
                      governorates.formatValue(row.sales),
                      orders,
                    );
                    return (
                      <li key={row.id} className="min-w-0">
                        <div className="flex min-w-0 items-baseline justify-between gap-3">
                          <span className="min-w-0 truncate text-[12px] text-[color:var(--text-secondary)]">
                            {row.name}
                          </span>
                          <span className="shrink-0 font-mono text-[12px] font-bold tabular-nums text-[color:var(--text-primary)]">
                            <bdi>{governorates.formatValue(row.sales)}</bdi>
                          </span>
                        </div>
                        {/* ONE hue at varying length: these bars encode
                            magnitude, not identity, so they must NOT borrow the
                            two-slot categorical series palette
                            (--chart-sales/--chart-profit) — that would imply
                            each governorate is a distinct measure.
                            --text-secondary is used as the ink because it is
                            the only single hue that clears 3:1 against the soft
                            track in BOTH themes (the data-viz gold sits at
                            ~1.8:1 on white and would fail). */}
                        <div
                          role="img"
                          aria-label={summary}
                          title={summary}
                          className="mt-1 h-[6px] w-full overflow-hidden rounded-full bg-[color:var(--track)]"
                        >
                          <div
                            aria-hidden="true"
                            className="h-full rounded-full bg-[color:var(--text-secondary)]"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="mt-0.5 text-[10.5px] text-[color:var(--text-muted)]">
                          <bdi>{orders}</bdi>
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}

          {paymentMix ? (
            <div className="min-w-0">
              <h3 className="text-[13px] font-bold text-[color:var(--text-primary)]">
                {paymentMix.title}
              </h3>
              <p className="mt-0.5 text-[11.5px] text-[color:var(--text-muted)]">
                {paymentMix.subtitle}
              </p>

              {mixTotal <= 0 ? (
                <p className="mt-2 text-[11.5px] leading-[1.5] text-[color:var(--text-muted)]">
                  {paymentMix.emptyLabel}
                </p>
              ) : (
                <>
                  {/* ONE continuous proportional bar — radius and clipping on
                      the container only, so the two rails read as parts of a
                      whole rather than as two chips. */}
                  <div className="mt-2.5 flex h-8 w-full overflow-hidden rounded-[8px]">
                    {paymentMix.slices.map((slice) => {
                      const share = slice.sales / mixTotal;
                      if (share <= 0) return null;
                      const summary = paymentMix.barSummary(
                        slice.label,
                        paymentMix.formatValue(slice.sales),
                        slice.ordersLabel,
                      );
                      return (
                        <div
                          key={slice.id}
                          role="img"
                          // Same string in both channels: `title` for the
                          // mouse, `aria-label` for keyboard/AT. Neither is the
                          // only carrier — the figures are text directly below.
                          aria-label={summary}
                          title={summary}
                          className="flex min-w-0 items-center justify-center"
                          style={{
                            flexGrow: share,
                            flexBasis: 0,
                            background: slice.color,
                          }}
                        >
                          {share >= MIN_LABELLED_SHARE ? (
                            <span
                              className={[
                                "truncate px-1.5 text-[11px] font-bold tabular-nums",
                              ].join(" ")}
                              style={{
                                color: slice.ink,
                              }}
                            >
                              <bdi>{Math.round(share * 100)}%</bdi>
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  {/* Every figure repeated as text — nothing colour-only,
                      nothing hover-only. The order count sits beside the money
                      rather than only in the bar's hover title: the rail that
                      collected more money is not necessarily the rail more
                      buyers chose, and that is the comparison being made. */}
                  <ul className="mt-2.5 flex flex-col gap-2">
                    {paymentMix.slices.map((slice) => (
                      <li
                        key={slice.id}
                        className="flex min-w-0 items-baseline justify-between gap-3"
                      >
                        <span className="inline-flex min-w-0 items-center gap-2 text-[12px] text-[color:var(--text-secondary)]">
                          <span
                            aria-hidden="true"
                            className="h-[9px] w-[9px] shrink-0 rounded-[3px]"
                            style={{ background: slice.color }}
                          />
                          <span className="truncate">{slice.label}</span>
                        </span>
                        <span className="flex shrink-0 items-baseline gap-1.5">
                          <span className="font-mono text-[12px] font-bold tabular-nums text-[color:var(--text-primary)]">
                            <bdi>{paymentMix.formatValue(slice.sales)}</bdi>
                          </span>
                          <span className="text-[10.5px] text-[color:var(--text-muted)]">
                            <bdi>{slice.ordersLabel}</bdi>
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
